/**
 * Fundação de áudio. NÃO há assets ainda — a API está pronta (canais Master/Music/SFX
 * com volumes persistidos). play*() é no-op enquanto não houver buffers registrados;
 * quando existirem, respeitam os volumes efetivos. Sem inventar arquivos.
 */
import { GameConfig } from "../config/GameConfig";
import { events } from "../core/Events";
import { SfxSynth } from "./SfxSynth";
import { MusicPlayer } from "./MusicPlayer";

export type AudioChannel = "master" | "music" | "sfx";

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export class AudioManager {
  private volumes: AudioSettings = { ...GameConfig.audio.defaults };
  private readonly buffers = new Map<string, AudioBuffer>();
  private ctx: AudioContext | null = null;
  /** 10.9HH — app em 2º plano: contexto travado — playSfx/playMusic viram no-op e ensureCtx NÃO religa. */
  private locked = false;
  private synth: SfxSynth | null = null;
  private music: MusicPlayer | null = null;
  private pendingRegion: string | null = null; // região pedida antes do ctx existir

  /** Garante o AudioContext (criado tarde: só após gesto do usuário — autoplay mobile). */
  private ensureCtx(): AudioContext | null {
    try {
      if (!this.ctx) {
        this.ctx = new AudioContext();
        this.synth = new SfxSynth(this.ctx);
        this.music = new MusicPlayer(this.ctx);
        this.music.setVolume(this.effective("music"));
      }
      if (this.ctx.state === "suspended" && !this.locked) void this.ctx.resume(); // 10.9HH
      return this.ctx;
    } catch {
      return null;
    }
  }

  /** Destrava o áudio num gesto do usuário (ex.: clicar em Jogar). Idempotente. */
  resume(): void {
    this.locked = false; // 10.9HH — voltou pro 1º plano
    this.ensureCtx();
    if (this.pendingRegion) {
      const r = this.pendingRegion;
      this.pendingRegion = null;
      this.playRegion(r);
    }
  }

  /** 10.5G — silencia TUDO (música + SFX) suspendendo o contexto (app em 2º plano). */
  suspend(): void {
    this.locked = true; // 10.9HH — qualquer som pedido em 2º plano é ignorado (antes o playSfx religava o contexto)
    try {
      if (this.ctx && this.ctx.state === "running") void this.ctx.suspend();
    } catch {
      /* ignora */
    }
  }

  /** Toca (em loop) a música procedural da região. */
  playRegion(regionId: string): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.music) {
      this.pendingRegion = regionId;
      return;
    }
    this.music.setVolume(this.effective("music"));
    void this.music.play(regionId);
  }

  stopMusic(): void {
    this.music?.stop();
  }

  getVolume(ch: AudioChannel): number {
    return this.volumes[ch];
  }

  setVolume(ch: AudioChannel, v: number): void {
    this.volumes[ch] = clamp01(v);
    this.music?.setVolume(this.effective("music")); // música acompanha master/music
    events.emit("settings:changed", undefined);
  }

  /** Volume efetivo de um canal (master × canal). */
  effective(ch: Exclude<AudioChannel, "master">): number {
    return this.volumes.master * this.volumes[ch];
  }

  /** Futuro: registrar um buffer decodificado para um id de som. */
  register(id: string, buffer: AudioBuffer): void {
    this.buffers.set(id, buffer);
  }

  /** Toca um SFX se houver buffer; caso contrário, no-op silencioso (sem assets ainda). */
  playSfx(id: string): void {
    this.playOn(id, "sfx");
  }

  playMusic(id: string): void {
    this.playOn(id, "music");
  }

  private playOn(id: string, ch: "sfx" | "music"): void {
    if (this.locked) return; // 10.9HH — em 2º plano: silêncio de verdade
    const ctx = this.ensureCtx();
    if (!ctx) return;
    const vol = this.effective(ch);
    if (vol <= 0) return;
    const buf = this.buffers.get(id);
    if (buf) {
      try {
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const gain = ctx.createGain();
        gain.gain.value = vol;
        src.connect(gain).connect(ctx.destination);
        src.start();
      } catch {
        /* ignora */
      }
      return;
    }
    // 10.4V: sem asset → sintetiza o som proceduralmente
    this.synth?.play(id, vol);
  }

  snapshot(): AudioSettings {
    return { ...this.volumes };
  }

  load(data: Partial<AudioSettings> | undefined): void {
    const d = GameConfig.audio.defaults;
    this.volumes = {
      master: clamp01(data?.master ?? d.master),
      music: clamp01(data?.music ?? d.music),
      sfx: clamp01(data?.sfx ?? d.sfx),
    };
  }

  reset(): void {
    this.volumes = { ...GameConfig.audio.defaults };
  }
}
